import { useMutation, useQuery } from '@tanstack/react-query'
import selectServices from '../../../../../../core/services/selectServices.ts'
import { useEffect, useMemo, useState } from 'react'
import useHandleAPIError, {
  TAPIError
} from '../../../../../../core/hooks/useHandleAPIError.ts'
import calculatorServices from '../../../services/calculatorServices.ts'
import { mapToOptions } from '@/core/utils/general.ts'
import { UseFormReturn } from 'react-hook-form'
import {
  calculatorFormSchema,
  ClientCreditDebt,
  Promotion,
  promotionApiResponseScehma
} from '@/modules/utilities/calculator/types/types.ts'
import { articleTypes } from '@/modules/sales/clients/types/articleTypes/articleTypeUtils.ts'
import { getRentabilityText } from '@/modules/utilities/calculator/components/calculator/hooks/utils.ts'
import useRentabilityLoaderAssign from '@/core/hooks/useRentabilityLoaderAssign/useRentabilityLoaderAssign.ts'

const useCalculatorForm = (
  form: UseFormReturn<calculatorFormSchema>,
  isOpen: boolean
) => {
  const { handleAPIError } = useHandleAPIError()
  const [articlePromotion, setArticlePromotion] = useState<
    Promotion | undefined
  >()

  const article = form.watch('article')

  const articleOthers = useMemo(() => {
    return article === articleTypes.others.toString()
  }, [article])

  const articleApple = useMemo(() => {
    return article === articleTypes.apple.toString()
  }, [article])

  const articleMoto = useMemo(() => {
    return article === articleTypes.motor.toString()
  }, [article])

  const {
    data: categories,
    isLoading: isLoadingCategories,
    error: errorCat
  } = useQuery({
    queryKey: ['categories'],
    queryFn: selectServices.getArticleCategoriesCatalog,
    enabled: isOpen
  })

  const {
    data: articles,
    isLoading: isLoadingArticles,
    error: errorArt
  } = useQuery({
    queryKey: ['articles'],
    queryFn: selectServices.getArticleTypes,
    enabled: isOpen
  })

  const {
    data: brands,
    isLoading: isLoadingBrands,
    error: errorBra
  } = useQuery({
    queryKey: ['brands'],
    queryFn: () => selectServices.getActiveBrands(),
    enabled: isOpen
  })

  const {
    data: models,
    mutate: searchModels,
    isPending: isLoadingModels
  } = useMutation({
    mutationKey: ['models'],
    mutationFn: (brandId: string) => selectServices.getActiveModels(brandId),
    onError: (error: TAPIError) => {
      handleAPIError(error, 'Error buscando modelos.')
    }
  })

  const {
    data: creditDebt,
    mutate: searchCreditDebt,
    isPending: isLoadingDebt
  } = useMutation({
    mutationKey: ['creditDebt'],
    mutationFn: (cedula: string) =>
      calculatorServices.getClientCreditDebit(cedula),
    onError: (error: TAPIError) => {
      handleAPIError(error, 'Error cargando los datos cliente.')
    },
    onSuccess: (data: ClientCreditDebt) => {
      if (data) {
        form.setValue('credit', data.credit.toString())
      }
    }
  })

  const { rentabilityLoader } = useRentabilityLoaderAssign(
    creditDebt?.rentabilityId,
    articlePromotion?.rentability.id,
    articleMoto ? 'MOTO' : 'PRODUCT'
  )

  const { mutate: promotionByCedule, isPending: isLoadingProm } = useMutation({
    mutationKey: ['promotionByCedule'],
    mutationFn: ({
      cedula,
      brandId,
      modelId
    }: {
      cedula: string
      brandId: string
      modelId: string
    }) => calculatorServices.getPromotionbyCedule(cedula, brandId, modelId),
    onError: (error: TAPIError) => {
      handleAPIError(error, 'Error cargando las promociones.')
    },
    onSuccess: (data: promotionApiResponseScehma) => {
      if (data?.promotion) {
        setArticlePromotion(data.promotion)
      } else {
        setArticlePromotion(undefined)
      }
    }
  })

  const {
    data: rentabilityCat,
    mutate: rentabilityBycategory,
    isPending: isLoadingCatRen
  } = useMutation({
    mutationKey: ['rentabilityBycategory'],
    mutationFn: (categoryId: string) =>
      selectServices.getRentabilitiesByCategory(categoryId),
    onError: (error: TAPIError) => {
      handleAPIError(error, 'Error cargando las rentabilidades.')
    }
  })

  const handleBuscar = (cedula: string) => {
    form.reset({
      cedula,
      article: '',
      category: '',
      brand: '',
      model: '',
      cost: '',
      rentability: '',
      payment_extra: ''
    })
    setArticlePromotion(undefined)
    searchCreditDebt(cedula)
  }

  const rentabilityOptions = useMemo(() => {
    if (articleOthers) {
      return rentabilityCat
        ? mapToOptions(rentabilityCat, getRentabilityText, 'id')
        : []
    }
    return rentabilityLoader.options
  }, [articleOthers, rentabilityLoader.options, rentabilityCat])

  useEffect(() => {
    if (errorCat) handleAPIError(errorCat, 'Error cargando las categorias')
  }, [errorCat, handleAPIError])

  useEffect(() => {
    if (errorArt) handleAPIError(errorArt, 'Error cargando los articulos')
  }, [errorArt, handleAPIError])

  useEffect(() => {
    if (errorBra) handleAPIError(errorBra, 'Error cargando las marcas')
  }, [errorBra, handleAPIError])

  useEffect(() => {
    if (rentabilityOptions.length > 0) {
      form.setValue('rentability', rentabilityOptions[0].id)
    }
  }, [rentabilityOptions])

  return {
    rentability: {
      options: rentabilityOptions,
      isLoading: rentabilityLoader.isLoading
    },
    categories: { options: categories || [], isLoading: isLoadingCategories },
    brands: { options: brands || [], isLoading: isLoadingBrands },
    models: { options: models || [], isLoading: isLoadingModels },
    articles: { options: articles || [], isLoading: isLoadingArticles },
    creditDebt,
    isLoadingDebt,
    handleBuscar,
    searchModels,
    promotionByCedule,
    isLoadingProm,
    articlePromotion,
    rentabilityBycategory,
    isLoadingCatRen,
    articleOthers,
    articleApple
  }
}

export default useCalculatorForm
